import * as THREE from "three";
import type { ModelCatalog } from "../catalog/schema";

/**
 * Build a stylised "Emma 1" children's care bed from primitives.
 * Mesh names match the meshName entries in public/models/emma-1.json.
 *
 * Coordinates (centimetres scaled to metres):
 *  - bed length (X): 140 cm + 8 cm overhang per side
 *  - bed width  (Z): 70 cm + 4 cm overhang per side
 *  - mattress top  : ~52 cm above floor
 */
export function buildEmma1(catalog: ModelCatalog): THREE.Group {
  const root = new THREE.Group();
  root.name = "emma-1";

  const colorOf = (mesh: string, fallback = 0xcccccc) => {
    const p = catalog.parts.find((p) => p.meshName === mesh);
    if (p?.color) return new THREE.Color(p.color);
    return new THREE.Color(fallback);
  };

  const mat = (mesh: string, opts: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
    new THREE.MeshStandardMaterial({
      color: colorOf(mesh),
      roughness: 0.55,
      metalness: 0.05,
      ...opts,
    });

  const matMetal = (mesh: string) => mat(mesh, { roughness: 0.32, metalness: 0.85 });
  const matSoft  = (mesh: string) => mat(mesh, { roughness: 0.85, metalness: 0.02 });

  const named = (name: string, geom: THREE.BufferGeometry, material: THREE.Material) => {
    const m = new THREE.Mesh(geom, material);
    m.name = name;
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  };

  // --- dimensions in metres ---
  const L = 1.50;     // outer length of frame (slightly larger than mattress)
  const W = 0.78;     // outer width
  const H = 0.52;     // mattress top height
  const railTop = H + 0.02;

  // ─── Beine (4) ──────────────────────────────────────────────────────
  const legGeom = new THREE.CylinderGeometry(0.022, 0.022, H - 0.07, 18);
  const legY = (H - 0.07) / 2 + 0.07;
  const dx = L / 2 - 0.06;
  const dz = W / 2 - 0.06;

  const beinVL = named("bein-vl", legGeom, mat("bein-vl"));
  beinVL.position.set( dx, legY, -dz);
  const beinVR = named("bein-vr", legGeom, mat("bein-vr"));
  beinVR.position.set( dx, legY,  dz);
  const beinHL = named("bein-hl", legGeom, mat("bein-hl"));
  beinHL.position.set(-dx, legY, -dz);
  const beinHR = named("bein-hr", legGeom, mat("bein-hr"));
  beinHR.position.set(-dx, legY,  dz);
  root.add(beinVL, beinVR, beinHL, beinHR);

  // ─── Rollen (caster wheels) ─────────────────────────────────────────
  const rollerGeom = new THREE.TorusGeometry(0.045, 0.015, 12, 28);
  const rollerY = 0.04;

  const mkRoller = (name: string, x: number, z: number) => {
    const r = named(name, rollerGeom, matMetal(name));
    r.position.set(x, rollerY, z);
    r.rotation.x = Math.PI / 2;
    return r;
  };
  root.add(
    mkRoller("rolle-vl",  dx, -dz),
    mkRoller("rolle-vr",  dx,  dz),
    mkRoller("rolle-hl", -dx, -dz),
    mkRoller("rolle-hr", -dx,  dz),
  );

  // ─── Seitenholm (links/rechts) — hollow rectangular profile ─────────
  const holmGeom = new THREE.BoxGeometry(L, 0.07, 0.05);
  const seitenholmL = named("seitenholm-l", holmGeom, mat("seitenholm-l"));
  seitenholmL.position.set(0, H - 0.04, -W / 2 + 0.04);
  const seitenholmR = named("seitenholm-r", holmGeom, mat("seitenholm-r"));
  seitenholmR.position.set(0, H - 0.04,  W / 2 - 0.04);
  root.add(seitenholmL, seitenholmR);

  // ─── Kopfteil & Fußteil — tall rounded panels ───────────────────────
  const panelShape = new THREE.Shape();
  const pw = W - 0.04, ph = 0.55, r = 0.10;
  panelShape.moveTo(-pw / 2 + r, 0);
  panelShape.lineTo( pw / 2 - r, 0);
  panelShape.absarc( pw / 2 - r, r, r, -Math.PI / 2, 0);
  panelShape.lineTo( pw / 2, ph - r);
  panelShape.absarc( pw / 2 - r, ph - r, r, 0, Math.PI / 2);
  panelShape.lineTo(-pw / 2 + r, ph);
  panelShape.absarc(-pw / 2 + r, ph - r, r, Math.PI / 2, Math.PI);
  panelShape.lineTo(-pw / 2, r);
  panelShape.absarc(-pw / 2 + r, r, r, Math.PI, 1.5 * Math.PI);
  const panelGeom = new THREE.ExtrudeGeometry(panelShape, {
    depth: 0.035, bevelEnabled: true, bevelThickness: 0.005, bevelSize: 0.005, bevelSegments: 2,
  });
  panelGeom.rotateY(Math.PI / 2);
  panelGeom.translate(0, H - 0.10, 0);

  const kopfteil = named("kopfteil", panelGeom.clone(), mat("kopfteil"));
  kopfteil.position.set( L / 2 + 0.005, 0, 0);
  const fussteil = named("fussteil", panelGeom.clone(), mat("fussteil"));
  fussteil.position.set(-L / 2 - 0.005, 0, 0);
  fussteil.rotation.y = Math.PI;
  root.add(kopfteil, fussteil);

  // ─── Lattenrost ─────────────────────────────────────────────────────
  const slatCount = 17;
  const slatLen = W - 0.18;
  const slatThick = 0.012;
  const slatWidth = 0.05;
  const slatGap = (L - 0.16) / slatCount;
  const lattenGroup = new THREE.Group();
  lattenGroup.name = "lattenrost";
  const lattenMat = mat("lattenrost");

  // Frame for the lattenrost
  const frameLong = new THREE.BoxGeometry(L - 0.10, 0.025, 0.04);
  const frameShort = new THREE.BoxGeometry(0.04, 0.025, W - 0.16);
  const fL = new THREE.Mesh(frameLong, lattenMat);
  fL.position.set(0, H - 0.08, -W / 2 + 0.10);
  fL.castShadow = true;
  fL.receiveShadow = true;
  const fR = new THREE.Mesh(frameLong, lattenMat);
  fR.position.set(0, H - 0.08,  W / 2 - 0.10);
  fR.castShadow = true;
  fR.receiveShadow = true;
  const fF = new THREE.Mesh(frameShort, lattenMat);
  fF.position.set( L / 2 - 0.07, H - 0.08, 0);
  fF.castShadow = true;
  fF.receiveShadow = true;
  const fB = new THREE.Mesh(frameShort, lattenMat);
  fB.position.set(-L / 2 + 0.07, H - 0.08, 0);
  fB.castShadow = true;
  fB.receiveShadow = true;
  lattenGroup.add(fL, fR, fF, fB);

  for (let i = 0; i < slatCount; i++) {
    const s = new THREE.Mesh(
      new THREE.BoxGeometry(slatWidth, slatThick, slatLen),
      lattenMat,
    );
    s.position.set(-L / 2 + 0.10 + slatGap / 2 + i * slatGap, H - 0.06, 0);
    s.castShadow = true;
    s.receiveShadow = true;
    lattenGroup.add(s);
  }
  root.add(lattenGroup);

  // ─── Matratze ───────────────────────────────────────────────────────
  const matrShape = new THREE.Shape();
  const mw = W - 0.20, ml = L - 0.18, mr = 0.06;
  matrShape.moveTo(-ml / 2 + mr, -mw / 2);
  matrShape.lineTo( ml / 2 - mr, -mw / 2);
  matrShape.absarc( ml / 2 - mr, -mw / 2 + mr, mr, -Math.PI / 2, 0);
  matrShape.lineTo( ml / 2,  mw / 2 - mr);
  matrShape.absarc( ml / 2 - mr,  mw / 2 - mr, mr, 0, Math.PI / 2);
  matrShape.lineTo(-ml / 2 + mr,  mw / 2);
  matrShape.absarc(-ml / 2 + mr,  mw / 2 - mr, mr, Math.PI / 2, Math.PI);
  matrShape.lineTo(-ml / 2, -mw / 2 + mr);
  matrShape.absarc(-ml / 2 + mr, -mw / 2 + mr, mr, Math.PI, 1.5 * Math.PI);
  const matrGeom = new THREE.ExtrudeGeometry(matrShape, {
    depth: 0.10, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 3,
  });
  matrGeom.rotateX(-Math.PI / 2);
  matrGeom.translate(0, H - 0.04, 0);
  const matratze = named("matratze", matrGeom, matSoft("matratze"));
  root.add(matratze);

  // ─── Sicherheitsgitter (links/rechts) ───────────────────────────────
  const gitterGroup = (side: "l" | "r") => {
    const g = new THREE.Group();
    g.name = side === "l" ? "gitter-l" : "gitter-r";
    const sign = side === "l" ? -1 : 1;
    const z = sign * (W / 2 - 0.025);
    const gitterMat = mat(g.name);

    // top + bottom rails
    const railLen = L - 0.30;
    const topRail = new THREE.Mesh(
      new THREE.CylinderGeometry(0.014, 0.014, railLen, 14),
      gitterMat,
    );
    topRail.rotation.z = Math.PI / 2;
    topRail.position.set(0, railTop + 0.32, z);
    topRail.castShadow = true; topRail.receiveShadow = true;
    const bottomRail = topRail.clone();
    bottomRail.position.set(0, railTop + 0.04, z);
    g.add(topRail, bottomRail);

    // verticals
    const vCount = 9;
    const step = railLen / (vCount + 1);
    for (let i = 1; i <= vCount; i++) {
      const v = new THREE.Mesh(
        new THREE.CylinderGeometry(0.011, 0.011, 0.30, 12),
        gitterMat,
      );
      v.position.set(-railLen / 2 + i * step, railTop + 0.18, z);
      v.castShadow = true; v.receiveShadow = true;
      g.add(v);
    }
    return g;
  };
  root.add(gitterGroup("l"), gitterGroup("r"));

  // ─── Gitter-Sprosse Set (Ersatz) ─ render compact stack at headboard
  const sprosseGroup = new THREE.Group();
  sprosseGroup.name = "gitter-spross-1";
  const sprMat = mat("gitter-spross-1");
  for (let i = 0; i < 5; i++) {
    const sp = new THREE.Mesh(
      new THREE.CylinderGeometry(0.010, 0.010, 0.28, 10),
      sprMat,
    );
    sp.rotation.z = Math.PI / 2;
    sp.position.set(L / 2 + 0.18 + i * 0.018, 0.04 + i * 0.022, 0);
    sp.castShadow = true; sp.receiveShadow = true;
    sprosseGroup.add(sp);
  }
  root.add(sprosseGroup);

  // ─── Eckverbinder Set (4 small L-brackets at corners, decorative) ───
  const eckGroup = new THREE.Group();
  eckGroup.name = "eckverbinder-1";
  const eckMat = matMetal("eckverbinder-1");
  const corners: Array<[number, number]> = [
    [ dx, -dz], [ dx,  dz], [-dx, -dz], [-dx,  dz],
  ];
  for (const [x, z] of corners) {
    const a = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.012, 0.012), eckMat);
    a.position.set(x - Math.sign(x) * 0.04, H - 0.04, z);
    a.castShadow = true; a.receiveShadow = true;
    const b = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.012, 0.06), eckMat);
    b.position.set(x, H - 0.04, z - Math.sign(z) * 0.04);
    b.castShadow = true; b.receiveShadow = true;
    eckGroup.add(a, b);
  }
  root.add(eckGroup);

  // ─── Schrauben-Set (small cluster on the floor near the foot end) ───
  const schraubGroup = new THREE.Group();
  schraubGroup.name = "schrauben-set";
  const schraubMat = matMetal("schrauben-set");
  for (let i = 0; i < 12; i++) {
    const sx = -L / 2 - 0.20 + (i % 4) * 0.025;
    const sz = -0.05 + Math.floor(i / 4) * 0.022;
    const head = new THREE.Mesh(
      new THREE.CylinderGeometry(0.008, 0.008, 0.005, 10),
      schraubMat,
    );
    head.position.set(sx, 0.012, sz);
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0035, 0.0025, 0.025, 10),
      schraubMat,
    );
    shaft.position.set(sx, 0.005, sz);
    head.castShadow = true; head.receiveShadow = true;
    shaft.castShadow = true; shaft.receiveShadow = true;
    schraubGroup.add(head, shaft);
  }
  root.add(schraubGroup);

  return root;
}

export const proceduralBuilders: Record<string, (c: ModelCatalog) => THREE.Group> = {
  "emma-1": buildEmma1,
};
